import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonList } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Dialog } from '@/shared/ui/Dialog';
import { useToast } from '@/shared/ui/Toast';
import {
  createExpense,
  deleteExpense,
  deleteReceipt,
  fetchReceiptBlob,
  listExpenses,
  listReceiptExpenseIds,
  uploadReceipt,
  type Expense,
} from '@/shared/api/expenses';
import { getMeeting, listParticipants } from '@/shared/api/meetings';
import { Icon, type IconName } from '@/shared/ui/Icon';
import { useAuthStore } from '@/shared/auth/store';
import { fmtKst } from '@/shared/lib/date';
import { formatKRW } from '@/shared/lib/money';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { CharSpringMorph } from '@/shared/ui/CharSpringMorph';
import { MoneyTabs } from '@/features/meeting/MoneyTabs';
import { apiErrorMessage } from '@/shared/api/client';

interface IconMeta {
  icon: IconName;
  label: string;
  bg: string;
  fg: string;
}

// v3 토널 카테고리 아이콘 (이모지·한 글자 마크 미사용)
const MEAL: IconMeta = { icon: 'receipt', label: '식사', bg: 'var(--category-meal-bg)', fg: 'var(--category-meal-fg)' };
const CAFE: IconMeta = { icon: 'coin', label: '카페', bg: 'var(--category-cafe-bg)', fg: 'var(--category-cafe-fg)' };
const ACTIVITY: IconMeta = { icon: 'map', label: '놀거리', bg: 'var(--category-activity-bg)', fg: 'var(--category-activity-fg)' };
const DEFAULT: IconMeta = { icon: 'wallet', label: '비용', bg: 'var(--category-default-bg)', fg: 'var(--category-default-fg)' };

function iconFor(expense: Expense): IconMeta {
  const desc = (expense.description ?? '').toLowerCase();
  if (/(커피|카페|cafe|coffee|음료|디저트)/i.test(desc)) return CAFE;
  if (/(식사|점심|저녁|맛집|라멘|밥|고기|우동|국수|치킨|피자|버거)/i.test(desc)) return MEAL;
  if (/(방탈출|게임|노래|볼링|당구|pc방|놀)/i.test(desc)) return ACTIVITY;
  if (expense.amount >= 50000) return MEAL;
  if (expense.amount >= 10000) return CAFE;
  return DEFAULT;
}

export default function ExpensePage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  // 결제 알림 자동입력(Android 래퍼)이 ?amount=&desc= 로 제안 값을 전달한다.
  // 제안은 프리필일 뿐, 등록은 항상 사용자가 확인한다 (자동 등록 금지).
  const [searchParams] = useSearchParams();
  const suggestedAmount = searchParams.get('amount') ?? '';
  const suggestedDesc = searchParams.get('desc') ?? '';
  const [amount, setAmount] = useState(
    /^\d+$/.test(suggestedAmount) ? suggestedAmount : '',
  );
  const [description, setDescription] = useState(suggestedDesc.slice(0, 200));
  const [paidBy, setPaidBy] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(() => /^\d+$/.test(suggestedAmount));
  // 분배 방식 — 기본 1/N, 직접 입력은 사람별 금액 지정 (0원 = 분배 제외)
  const [splitMode, setSplitMode] = useState<'equal' | 'custom'>('equal');
  const [customAmounts, setCustomAmounts] = useState<Record<string, string>>({});
  const user = useAuthStore((s) => s.user);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const partsQ = useQuery({
    queryKey: ['participants', id],
    queryFn: () => listParticipants(id),
    enabled: !!id,
  });
  const meetingQ = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => getMeeting(id),
    enabled: !!id,
  });
  const expQ = useQuery({
    queryKey: ['expenses', id],
    queryFn: () => listExpenses(id),
    enabled: !!id,
  });
  const receiptsQ = useQuery({
    queryKey: ['receipts', id],
    queryFn: () => listReceiptExpenseIds(id),
    enabled: !!id,
  });
  const receiptImgQ = useQuery({
    queryKey: ['receipt-img', viewingId],
    queryFn: () => fetchReceiptBlob(viewingId!),
    enabled: !!viewingId,
    staleTime: 60_000,
  });
  const receiptUrl = useMemo(
    () => (receiptImgQ.data ? URL.createObjectURL(receiptImgQ.data) : null),
    [receiptImgQ.data],
  );
  useEffect(() => {
    return () => {
      if (receiptUrl) URL.revokeObjectURL(receiptUrl);
    };
  }, [receiptUrl]);

  const createMut = useMutation({
    mutationFn: () =>
      createExpense(id, {
        amount: Number(amount),
        ...(description ? { description } : {}),
        ...(paidBy ? { paidByUserId: paidBy } : {}),
        ...(splitMode === 'custom' ? { shares: buildCustomShares() } : {}),
      }),
    onSuccess: () => {
      setAmount('');
      setDescription('');
      setPaidBy('');
      setAmountError(null);
      setAdding(false);
      setSplitMode('equal');
      setCustomAmounts({});
      qc.invalidateQueries({ queryKey: ['expenses', id] });
      toast.success('비용을 등록했어요.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, '비용 등록에 실패했어요.')),
  });
  const uploadReceiptMut = useMutation({
    mutationFn: ({ expenseId, file }: { expenseId: string; file: File }) =>
      uploadReceipt(expenseId, file),
    onSuccess: (receipt) => {
      qc.invalidateQueries({ queryKey: ['receipts', id] });
      qc.invalidateQueries({ queryKey: ['receipt-img', receipt.expenseId] });
      toast.success('영수증을 첨부했어요.');
    },
    onError: () => toast.error('영수증 업로드에 실패했어요. (이미지 5MB 이하)'),
  });
  const deleteReceiptMut = useMutation({
    mutationFn: deleteReceipt,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receipts', id] });
      setViewingId(null);
      toast.show('영수증을 삭제했어요.');
    },
    onError: () => toast.error('영수증 삭제에 실패했어요.'),
  });
  const deleteMut = useMutation({
    mutationFn: deleteExpense,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses', id] });
      setDeletingId(null);
      toast.success('비용을 삭제했어요.');
    },
    onError: () => toast.error('비용 삭제에 실패했어요.'),
  });

  function onCreate(e: FormEvent) {
    e.preventDefault();
    const n = Number(amount);
    if (!Number.isFinite(n) || n < 1) {
      setAmountError('1원 이상의 금액을 입력해주세요.');
      return;
    }
    if (splitMode === 'custom' && customRemaining !== 0) {
      setAmountError(null);
      toast.error('분담액 합계가 총액과 일치해야 해요.');
      return;
    }
    setAmountError(null);
    createMut.mutate();
  }

  const activeParts = partsQ.data?.filter((p) => !p.leftAt) ?? [];
  const participantCount = activeParts.length;
  const perPersonPreview = useMemo(() => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0 || participantCount === 0) return null;
    return Math.floor(n / participantCount);
  }, [amount, participantCount]);

  // 직접 입력 분배 — 합계가 총액과 같아야 등록 가능
  const customSum = activeParts.reduce(
    (sum, p) => sum + (Number(customAmounts[p.userId]) || 0),
    0,
  );
  const customRemaining = (Number(amount) || 0) - customSum;

  function buildCustomShares() {
    const payerId = paidBy || user?.id || '';
    const rows = activeParts
      .map((p) => ({ userId: p.userId, shareAmount: Number(customAmounts[p.userId]) || 0 }))
      .filter((r) => r.shareAmount > 0);
    // 백엔드 규칙: 잔액 인수자(bearsRemainder)는 정확히 1인 — 결제자 우선, 없으면 첫 행
    const remainderIdx = Math.max(rows.findIndex((r) => r.userId === payerId), 0);
    return rows.map((r, i) => ({ ...r, bearsRemainder: i === remainderIdx }));
  }

  function fillEvenly() {
    const total = Number(amount) || 0;
    if (total <= 0 || activeParts.length === 0) return;
    const base = Math.floor(total / activeParts.length);
    const payerId = paidBy || user?.id;
    const holder = activeParts.find((p) => p.userId === payerId) ?? activeParts[0];
    const next: Record<string, string> = {};
    for (const p of activeParts) next[p.userId] = String(base);
    next[holder.userId] = String(base + (total - base * activeParts.length));
    setCustomAmounts(next);
  }

  const expenses = expQ.data ?? [];
  const total = expenses.reduce((sum, e) => sum + e.amount, 0);
  const averagePerPerson = participantCount > 0 ? Math.floor(total / participantCount) : 0;

  const nicknameOf = (uid: string) =>
    partsQ.data?.find((p) => p.userId === uid)?.nickname ?? uid.slice(-4);

  const hostUserId = partsQ.data?.find((p) => p.role === 'HOST')?.userId;
  const canEditExpenses = meetingQ.data?.status !== 'CLOSED';
  const canDeleteExpense = (e: Expense) =>
    canEditExpenses && (user?.id === e.paidByUserId || user?.id === hostUserId);
  const receiptIds = new Set(receiptsQ.data ?? []);
  const canAttach = (e: Expense) =>
    canEditExpenses && (user?.id === e.paidByUserId || user?.id === hostUserId);

  function pickReceiptFor(expenseId: string) {
    uploadTargetRef.current = expenseId;
    fileInputRef.current?.click();
  }

  function onReceiptFilePicked(file: File | null) {
    const expenseId = uploadTargetRef.current;
    uploadTargetRef.current = null;
    if (!file || !expenseId) return;
    uploadReceiptMut.mutate({ expenseId, file });
  }

  return (
    <section className="space-y-6">
      <h1 className="sr-only">비용</h1>
      <MeetingTabs meetingId={id} />
      <MoneyTabs meetingId={id} />

      <section className="relative overflow-hidden rounded-[22px] bg-[image:var(--money-gradient-expense)] px-4 py-4 text-white shadow-[var(--money-glow)]">
        <div className="absolute -right-8 -top-10 h-32 w-32 rounded-full bg-primary-light/25" aria-hidden="true" />
        <div className="absolute bottom-3 right-6 h-16 w-16 rounded-full border border-primary-light/25 bg-primary/20" aria-hidden="true" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/70">쓴 돈 요약</p>
            <CharSpringMorph
              value={formatKRW(total)}
              gap={0}
              animateOnMount
              className="num-display mt-2 text-[31px] leading-none tracking-[-0.8px] text-white"
            />
            <p className="mt-2 text-[12px] font-medium text-white/75">
              {participantCount > 0 ? `${participantCount}명이 함께 나누면 1인당 ${formatKRW(averagePerPerson)}` : '참여자 정보를 불러오는 중…'}
            </p>
          </div>
          <div className="relative shrink-0 overflow-hidden rounded-[18px] border border-primary-light/25 bg-primary-light/20 px-3.5 py-3 text-right shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] backdrop-blur">
            <div className="absolute -right-4 -top-5 h-12 w-12 rounded-full bg-primary-light/30" aria-hidden="true" />
            <p className="relative text-[10px] font-semibold text-white/72">등록 내역</p>
            <p className="relative mt-1 text-[22px] font-semibold leading-none text-white">{expenses.length}건</p>
          </div>
        </div>
      </section>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-medium text-ink-faint">전체 현황</p>
          <h2 className="text-feature font-semibold tracking-tight-feature text-ink">비용 내역</h2>
        </div>
        {expenses.length > 0 ? (
          <Button variant="brand" onClick={() => setAdding(true)} disabled={!canEditExpenses} aria-label="비용 추가" className="gap-1.5 px-4">
            <Icon name="plus" size={16} />
            비용 등록
          </Button>
        ) : null}
      </div>

      {expQ.isLoading ? <SkeletonList count={2} /> : null}
      {expQ.isError ? (
        <ErrorState title="비용 목록을 불러오지 못했어요" onRetry={() => expQ.refetch()} />
      ) : null}

      {expQ.data && expenses.length === 0 ? (
        <EmptyState
          title="아직 비용이 없어요"
          description="첫 비용을 입력해 1/N 분배를 시작하세요."
          action={
            <Button variant="brand" onClick={() => setAdding(true)} disabled={!canEditExpenses} className="gap-1.5">
              <Icon name="plus" size={16} />
              첫 비용 등록
            </Button>
          }
        />
      ) : null}

      {expenses.length > 0 ? (
        <ul className="space-y-2" aria-label="비용 목록">
          {expenses.map((e) => {
            const icon = iconFor(e);
            const hasReceipt = receiptIds.has(e.id);
            return (
              <li
                key={e.id}
                className="rounded-[18px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm px-3 py-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[11px]"
                      style={{ backgroundColor: icon.bg, color: icon.fg }}
                      aria-label={icon.label}
                    >
                      <Icon name={icon.icon} size={17} />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-ui font-semibold text-ink">{e.description ?? '비용'}</p>
                      <p className="mt-0.5 truncate text-small text-ink-muted">
                        {nicknameOf(e.paidByUserId)} 결제 · {fmtKst(e.occurredAt, 'M월 d일')}
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="num-display text-[18px] leading-none text-ink">{formatKRW(e.amount)}</p>
                    <div className="mt-1 flex items-center justify-end gap-1.5">
                      {hasReceipt ? (
                        <button
                          type="button"
                          onClick={() => setViewingId(e.id)}
                          aria-label={`${e.description ?? '비용'} 영수증 보기`}
                          className="flex h-11 w-11 items-center justify-center rounded-full text-ink-muted transition-colors duration-200 ease-airbnb hover:bg-surface-muted hover:text-ink focus-visible:outline-none focus-visible:shadow-focus-ring"
                        >
                          <Icon name="receipt" size={17} />
                        </button>
                      ) : canAttach(e) ? (
                        <button
                          type="button"
                          onClick={() => pickReceiptFor(e.id)}
                          disabled={uploadReceiptMut.isPending}
                          aria-label={`${e.description ?? '비용'} 영수증 첨부`}
                          className="flex h-11 w-11 items-center justify-center rounded-full text-ink-muted transition-colors duration-200 ease-airbnb hover:bg-surface-muted hover:text-ink focus-visible:outline-none focus-visible:shadow-focus-ring"
                        >
                          <Icon name="camera" size={17} />
                        </button>
                      ) : null}
                      {canDeleteExpense(e) ? (
                        <button
                          type="button"
                          onClick={() => setDeletingId(e.id)}
                          aria-label={`${e.description ?? '비용'} 삭제`}
                          className="flex h-11 w-11 items-center justify-center rounded-full text-ink-muted transition-colors duration-200 ease-airbnb hover:bg-surface-muted hover:text-error focus-visible:outline-none focus-visible:shadow-focus-ring"
                        >
                          <Icon name="x" size={16} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      <Dialog
        open={adding}
        title="비용 등록"
        confirmLabel={createMut.isPending ? '등록 중…' : '등록'}
        confirmDisabled={
          createMut.isPending ||
          !amount ||
          Number(amount) < 1 ||
          (splitMode === 'custom' && (customRemaining !== 0 || customSum === 0))
        }
        onConfirm={() => onCreate({ preventDefault: () => {} } as FormEvent)}
        onCancel={() => {
          if (createMut.isPending) return;
          setAdding(false);
          setAmountError(null);
        }}
      >
        <form onSubmit={onCreate} className="space-y-3" aria-label="비용 등록">
          {/* 콤마 표시 입력 — state는 숫자만 보관, 표시만 천단위 구분 */}
          <Input
            label="금액 (원)"
            inputMode="numeric"
            placeholder="38,500"
            value={amount ? Number(amount).toLocaleString('ko-KR') : ''}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, '').slice(0, 9))}
            required
            aria-required="true"
            error={amountError}
            autoFocus
          />
          <Input
            label="설명"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={200}
            placeholder="예) 성수 라멘 공방"
          />
          <div>
            <label htmlFor="paid-by" className="mb-1.5 block text-small font-medium text-ink-muted">
              결제자
            </label>
            <select
              id="paid-by"
              className="min-h-[48px] w-full rounded-lg border border-surface-border bg-surface px-4 py-2.5 text-ui text-ink transition-all duration-200 ease-airbnb focus-visible:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/10"
              value={paidBy}
              onChange={(e) => setPaidBy(e.target.value)}
            >
              <option value="">본인</option>
              {activeParts.map((p) => (
                <option key={p.userId} value={p.userId}>
                  {p.nickname}
                </option>
              ))}
            </select>
          </div>
          {/* 분배 방식 — 카카오 정산하기 문법: 1/N 기본 + 사람별 직접 입력 */}
          <div
            role="radiogroup"
            aria-label="분배 방식"
            className="flex gap-1 rounded-full border border-surface-border/70 bg-surface-muted/70 p-1"
          >
            {(
              [
                ['equal', '똑같이 나누기'],
                ['custom', '직접 입력'],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={splitMode === mode}
                onClick={() => setSplitMode(mode)}
                className={`flex-1 rounded-full px-3 py-2 text-small font-semibold transition-colors duration-200 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                  splitMode === mode ? 'bg-[image:var(--hero-grad)] text-white shadow-[var(--hero-glow)]' : 'text-ink-muted'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {splitMode === 'equal' && perPersonPreview !== null ? (
            <p
              aria-live="polite"
              className="rounded-lg bg-primary-soft px-3.5 py-2.5 text-small font-medium text-primary-strong"
            >
              1/{participantCount} 예상 분배 · 1인당 {formatKRW(perPersonPreview)}
              <span className="text-ink-muted"> (잔액은 결제자 부담)</span>
            </p>
          ) : null}

          {splitMode === 'custom' ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-small font-medium text-ink-muted">사람별 금액 (0원은 제외)</span>
                <button
                  type="button"
                  onClick={fillEvenly}
                  className="text-small font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:underline"
                >
                  똑같이 채우기
                </button>
              </div>
              <ul className="space-y-1.5">
                {activeParts.map((p) => (
                  <li key={p.userId} className="flex items-center justify-between gap-3">
                    <span className="min-w-0 truncate text-body text-ink">{p.nickname}</span>
                    <input
                      inputMode="numeric"
                      aria-label={`${p.nickname} 분담액`}
                      placeholder="0"
                      value={
                        customAmounts[p.userId]
                          ? Number(customAmounts[p.userId]).toLocaleString('ko-KR')
                          : ''
                      }
                      onChange={(e) =>
                        setCustomAmounts({
                          ...customAmounts,
                          [p.userId]: e.target.value.replace(/[^0-9]/g, '').slice(0, 9),
                        })
                      }
                      className="w-32 shrink-0 rounded-lg border border-surface-border bg-surface px-3 py-2 text-right text-ui tabular-nums text-ink focus-visible:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/10"
                    />
                  </li>
                ))}
              </ul>
              <p
                aria-live="polite"
                className={`rounded-lg px-3.5 py-2.5 text-small font-medium ${
                  customRemaining === 0 && customSum > 0
                    ? 'bg-primary-soft text-primary-strong'
                    : 'bg-surface-muted text-ink-muted'
                }`}
              >
                {customRemaining === 0 && customSum > 0
                  ? `합계 ${formatKRW(customSum)} — 총액과 일치해요`
                  : customRemaining === 0
                    ? '금액을 입력해주세요'
                    : customRemaining > 0
                      ? `남은 금액 ${formatKRW(customRemaining)}`
                      : `총액보다 ${formatKRW(Math.abs(customRemaining))} 많아요`}
              </p>
            </div>
          ) : null}
        </form>
      </Dialog>

      {/* 영수증 업로드용 숨김 파일 입력 (행의 카메라 버튼이 트리거) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          onReceiptFilePicked(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />

      <Dialog
        open={viewingId !== null}
        title="영수증"
        confirmLabel="닫기"
        onConfirm={() => setViewingId(null)}
        onCancel={() => setViewingId(null)}
      >
        {receiptImgQ.isLoading ? (
          <p className="text-body text-ink-muted">불러오는 중…</p>
        ) : receiptImgQ.isError ? (
          <p className="text-body text-error" role="alert">
            영수증을 불러오지 못했어요.
          </p>
        ) : receiptUrl ? (
          <div className="space-y-3">
            <img
              src={receiptUrl}
              alt="영수증 사진"
              className="max-h-[60vh] w-full rounded-lg object-contain"
            />
            {viewingId &&
            expenses.some((e) => e.id === viewingId && canAttach(e)) ? (
              <Button
                variant="danger"
                className="w-full"
                disabled={deleteReceiptMut.isPending}
                onClick={() => deleteReceiptMut.mutate(viewingId)}
              >
                {deleteReceiptMut.isPending ? '삭제 중…' : '영수증 삭제'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={deletingId !== null}
        title="이 비용을 삭제할까요?"
        description="삭제한 비용은 되돌릴 수 없어요."
        confirmLabel="삭제"
        confirmTone="danger"
        onConfirm={() => deletingId && deleteMut.mutate(deletingId)}
        confirmDisabled={deleteMut.isPending}
        onCancel={() => setDeletingId(null)}
      />
    </section>
  );
}
